import React, { useState, useEffect } from "react";
import API_BASE_URL from"../Config/config";
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Grid,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, api } from "../context/AuthContext";

function AdminRequest() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [existingRequest, setExistingRequest] = useState(null);

  const [formData, setFormData] = useState({
    companyName: "",
    companyAddress: "",
    companyWebsite: "",
    contactPhone: "",
    businessRegistrationNumber: "",
    companyCeoName: "",
    companyBoardMembers: "",
    companySpecialization: "",
    registrationFile: null,
    companyDocuments: null,
  });

  const [selectedFiles, setSelectedFiles] = useState({
    registrationFile: null,
    companyDocuments: null,
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const fetchExistingRequest = async () => {
      try {
        const response = await api.get("/admin-requests/my-request");
        if (response.data) setExistingRequest(response.data);
      } catch (err) {
        console.error("Error fetching existing request:", err);
      } finally {
        setFetching(false);
      }
    };

    fetchExistingRequest();
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files && files.length > 0) {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0],
      }));

      setSelectedFiles((prev) => ({
        ...prev,
        [name]: files[0].name,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const data = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== "") {
          console.log(`Appending to FormData: ${key}`, value);
          data.append(key, value);
        }
      });

      console.log("Submitting FormData to /admin-requests");
      const response = await api.post("/admin-requests", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Mark that we are coming from admin request for the pricing page
      localStorage.setItem('fromAdminRequest', 'true');
      
      setSuccess("Your admin request has been submitted. Now redirecting to payment...");
      setExistingRequest(response.data);
      
      // Navigate to pricing after successful submission
      setTimeout(() => {
        navigate('/pricing');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (user?.role === "admin" || user?.role === "super_admin") {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 8, textAlign: "center" }}>
          <Alert severity="info">You already have admin privileges.</Alert>
          <Button sx={{ mt: 2 }} onClick={() => navigate("/")}>
            Go Home
          </Button>
        </Box>
      </Container>
    );
  }

  if (existingRequest) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 8 }}>
          <Paper sx={{ p: 4, borderRadius: 2, border: "1px solid rgba(79, 70, 229, 0.2)" }}>
            <Typography variant="h4" mb={3} align="center" sx={{ color: "#4F46E5" }}>
              Admin Request Status
            </Typography>

            <Alert
              severity={
                existingRequest.status === "pending"
                  ? "info"
                  : existingRequest.status === "approved"
                  ? "success"
                  : "error"
              }
            >
              Status: {existingRequest.status.toUpperCase()}
            </Alert>

            <Box sx={{ mt: 3 }}>
              <Typography><strong>Company:</strong> {existingRequest.company_name}</Typography>
              <Typography><strong>CEO:</strong> {existingRequest.company_ceo_name}</Typography>
              <Typography><strong>Specialization:</strong> {existingRequest.company_specialization}</Typography>
              <Typography><strong>Board Members:</strong> {existingRequest.company_board_members}</Typography>
              <Typography><strong>Phone:</strong> {existingRequest.contact_phone}</Typography>
              
              {existingRequest.documents && existingRequest.documents.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1"><strong>Uploaded Documents:</strong></Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {existingRequest.documents.map((doc, index) => (
                      <Button
                        key={index}
                        variant="outlined"
                        size="small"
                        component="a"
                        href={`${API_BASE_URL}${doc.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: 'none' }}
                      >
                        {doc.name}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            {existingRequest.admin_feedback && (
              <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 2 }}>
                <Typography color="error">
                  <strong>Feedback:</strong> {existingRequest.admin_feedback}
                </Typography>
              </Box>
            )}

            {existingRequest.status === "rejected" && (
              <Button sx={{ mt: 3 }} variant="contained" onClick={() => setExistingRequest(null)}>
                Submit New Request
              </Button>
            )}
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper
          sx={{
            p: 4,
            borderRadius: 2,
            border: "1px solid rgba(79, 70, 229, 0.2)",
            boxShadow: "0 10px 40px rgba(79, 70, 229, 0.15), 0 20px 60px rgba(96, 165, 250, 0.12)",
          }}
        >
          <Typography variant="h4" mb={3} align="center" sx={{ color: "#4F46E5" }}>
            Recruiter Request Form
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Company Name" name="companyName" required onChange={handleChange} />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Address"
                  name="companyAddress"
                  multiline
                  rows={3}
                  required
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Company CEO Name" name="companyCeoName" required onChange={handleChange} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Company Specialization" name="companySpecialization" required onChange={handleChange} />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Board Members"
                  name="companyBoardMembers"
                  helperText="Separate names with commas"
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Company Website" name="companyWebsite" onChange={handleChange} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Contact Phone" name="contactPhone" required onChange={handleChange} />
              </Grid>

              <Grid item xs={12}>
                <TextField fullWidth label="Business Registration Number" name="businessRegistrationNumber" onChange={handleChange} />
              </Grid>

              {/* FILE UPLOADS */}
              <Grid item xs={12}>
                <Button variant="outlined" component="label" fullWidth>
                  Upload Company Registration File
                  <input type="file" hidden name="registrationFile" onChange={handleChange} />
                </Button>
                {selectedFiles.registrationFile && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    📎 {selectedFiles.registrationFile}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Button variant="outlined" component="label" fullWidth>
                  Upload Company Documents
                  <input type="file" hidden name="companyDocuments" onChange={handleChange} />
                </Button>
                {selectedFiles.companyDocuments && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    📎 {selectedFiles.companyDocuments}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Button type="submit" fullWidth variant="contained" size="large" disabled={loading}>
                  {loading ? <CircularProgress size={24} /> : "Submit Request"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </Container>
  );
}

export default AdminRequest;
